import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Typewriter hook driven by requestAnimationFrame.
 *
 * Each character takes ~18ms (configurable). Returns:
 *   { progress, start, stop }
 *
 * - progress: number in [0, 1]
 * - start(textLength, onComplete?): kicks off the animation for the given length
 * - stop(): cancels any in-flight RAF loop and resets progress to 0
 *
 * Honours prefers-reduced-motion by snapping progress to 1 immediately.
 */
export function useTypewriter({ msPerChar = 18 } = {}) {
  const [progress, setProgress] = useState(0);

  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const totalDurationRef = useRef(0);
  const onCompleteRef = useRef(null);
  const lengthRef = useRef(0);

  const cancel = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback((now) => {
    const elapsed = now - startTimeRef.current;
    const total = totalDurationRef.current;
    if (total <= 0) {
      setProgress(1);
      rafRef.current = null;
      if (typeof onCompleteRef.current === 'function') onCompleteRef.current();
      return;
    }
    const p = Math.min(1, elapsed / total);
    setProgress(p);
    if (p >= 1) {
      rafRef.current = null;
      if (typeof onCompleteRef.current === 'function') onCompleteRef.current();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback((textLength, onComplete) => {
    cancel();
    const len = Math.max(0, Number(textLength) | 0);
    lengthRef.current = len;
    onCompleteRef.current = typeof onComplete === 'function' ? onComplete : null;

    // Reduced-motion users: skip the animation entirely.
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || len === 0) {
      setProgress(1);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    setProgress(0);
    totalDurationRef.current = len * msPerChar;
    startTimeRef.current = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [cancel, msPerChar, tick]);

  const stop = useCallback(() => {
    cancel();
    setProgress(0);
  }, [cancel]);

  // Snap progress to 1 immediately, fire onComplete, and clear any in-flight RAF.
  const finishNow = useCallback(() => {
    cancel();
    setProgress(1);
    const cb = onCompleteRef.current;
    onCompleteRef.current = null;
    if (typeof cb === 'function') cb();
  }, [cancel]);

  useEffect(() => () => cancel(), [cancel]);

  return { progress, start, stop, finishNow };
}

export default useTypewriter;
